import React, { useState, useEffect } from 'react';
import { crmService } from '../../services/crmService';

export default function InsertGovOpportunity() {
  const [formData, setFormData] = useState({
    title: '',
    agency: '',
    description: '',
    opportunity_type: 'RFP',
    posted_date: new Date().toISOString().split('T')[0],
    response_deadline: '',
    opportunity_value_usd: '',
    psc_code: '',
    naics_codes: [''],
    source_url: '',
  });

  const [verticals, setVerticals] = useState([]);
  const [selectedVerticals, setSelectedVerticals] = useState([]);
  const [selectedCollaborators, setSelectedCollaborators] = useState([]);
  const [pscQuery, setPscQuery] = useState('');
  const [pscResults, setPscResults] = useState([]);
  const [govEstimate, setGovEstimate] = useState(null);
  const [addFollowOn, setAddFollowOn] = useState(false);
  const [followOnForm, setFollowOnForm] = useState({
    title: '',
    agency: '',
    response_deadline: '',
    opportunity_value_usd: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [createdOpportunityId, setCreatedOpportunityId] = useState(null);

  const collaboratorOptions = [
    'portfolio_manager',
    'platform_integrator',
    'doctrine_writer',
    'policy_expert',
    'end_user',
  ];

  const collaboratorLabels = {
    portfolio_manager: 'Portfolio Manager',
    platform_integrator: 'Platform Integrator',
    doctrine_writer: 'Doctrine Writer',
    policy_expert: 'Policy Expert',
    end_user: 'End User',
  };

  useEffect(() => {
    crmService.getVerticals().then(setVerticals).catch(console.error);
  }, []);

  // PSC search for the picker
  useEffect(() => {
    if (!pscQuery.trim()) { setPscResults([]); return; }
    const t = setTimeout(() => {
      crmService.searchPSC(pscQuery.trim())
        .then(rows => setPscResults(Array.isArray(rows) ? rows.slice(0, 8) : []))
        .catch(() => setPscResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [pscQuery]);

  // Live value estimate from PSC + agency (office), refreshed when either changes.
  useEffect(() => {
    if (!formData.psc_code) { setGovEstimate(null); return; }
    const t = setTimeout(() => {
      crmService.estimateGovOpportunity({ psc_code: formData.psc_code, agency: formData.agency })
        .then(setGovEstimate).catch(() => setGovEstimate(null));
    }, 300);
    return () => clearTimeout(t);
  }, [formData.psc_code, formData.agency]);

  const fmtUsd = (v) => v == null ? '—' : (v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${v}`);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleNaicsChange = (index, value) => {
    const newNaics = [...formData.naics_codes];
    newNaics[index] = value;
    setFormData(prev => ({ ...prev, naics_codes: newNaics }));
  };

  const addNaicsField = () => {
    setFormData(prev => ({ ...prev, naics_codes: [...prev.naics_codes, ''] }));
  };

  const removeNaicsField = (index) => {
    setFormData(prev => ({
      ...prev,
      naics_codes: prev.naics_codes.filter((_, i) => i !== index)
    }));
  };

  const toggleVertical = (verticalId) => {
    setSelectedVerticals(prev =>
      prev.includes(verticalId)
        ? prev.filter(id => id !== verticalId)
        : [...prev, verticalId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.title || !formData.agency || !formData.response_deadline) {
        throw new Error('Title, Agency, and Deadline are required');
      }

      const matchedVerticals = verticals
        .filter(v => selectedVerticals.includes(v.id))
        .map(v => ({ id: v.id, name: v.name }));

      const payload = {
        ...formData,
        naics_codes: formData.naics_codes.filter(n => n.trim()),
        matched_verticals: matchedVerticals,
        collaboration_partners: selectedCollaborators,
      };

      const result = await crmService.createGovOpportunity(payload);
      setCreatedOpportunityId(result.opportunity.id);

      if (addFollowOn) {
        // Show follow-on form after parent is created
        setSuccess(`✅ Parent opportunity created: "${result.opportunity.title}"\n\nNow add the follow-on opportunity`);
      } else {
        setSuccess(`✅ Opportunity created: "${result.opportunity.title}"`);
        setFormData({
          title: '',
          agency: '',
          description: '',
          opportunity_type: 'RFP',
          posted_date: new Date().toISOString().split('T')[0],
          response_deadline: '',
          opportunity_value_usd: '',
          psc_code: '',
          naics_codes: [''],
          source_url: '',
        });
        setGovEstimate(null);
        setSelectedVerticals([]);
        setSelectedCollaborators([]);
        setAddFollowOn(false);
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      setError(`❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowOnSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!followOnForm.title || !followOnForm.agency || !followOnForm.response_deadline) {
        throw new Error('Title, Agency, and Deadline are required for follow-on');
      }

      await crmService.addFollowOnOpportunity(createdOpportunityId, {
        ...followOnForm,
        collaboration_partners: selectedCollaborators,
      });

      setSuccess(`✅ Follow-on opportunity created successfully!`);
      setFormData({
        title: '',
        agency: '',
        description: '',
        opportunity_type: 'RFP',
        posted_date: new Date().toISOString().split('T')[0],
        response_deadline: '',
        opportunity_value_usd: '',
        naics_codes: [''],
        source_url: '',
      });
      setFollowOnForm({
        title: '',
        agency: '',
        response_deadline: '',
        opportunity_value_usd: '',
      });
      setSelectedVerticals([]);
      setSelectedCollaborators([]);
      setAddFollowOn(false);
      setCreatedOpportunityId(null);

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(`❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#0A0F1C', color: '#F1F5F9' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #1E2D45', flexShrink: 0 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#22D3EE', marginBottom: 8 }}>
          ➕ Add Government Opportunity
        </h2>
        <p style={{ margin: 0, fontSize: 12, color: '#64748B' }}>
          Manually add contract opportunities not found in SAM.gov
        </p>
      </div>

      {/* Parent Opportunity Form */}
      {!createdOpportunityId && (
      <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Basic Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="RFP Title"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 4,
                background: '#111827',
                border: '1px solid #1E2D45',
                color: '#F1F5F9',
                fontSize: 12,
                fontFamily: 'inherit',
              }}
              required
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
              Agency *
            </label>
            <input
              type="text"
              name="agency"
              value={formData.agency}
              onChange={handleChange}
              placeholder="e.g., Department of War"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 4,
                background: '#111827',
                border: '1px solid #1E2D45',
                color: '#F1F5F9',
                fontSize: 12,
                fontFamily: 'inherit',
              }}
              required
            />
          </div>
        </div>

        {/* Dates and Type */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
              Posted Date
            </label>
            <input
              type="date"
              name="posted_date"
              value={formData.posted_date}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 4,
                background: '#111827',
                border: '1px solid #1E2D45',
                color: '#F1F5F9',
                fontSize: 12,
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
              Response Deadline *
            </label>
            <input
              type="date"
              name="response_deadline"
              value={formData.response_deadline}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 4,
                background: '#111827',
                border: '1px solid #1E2D45',
                color: '#F1F5F9',
                fontSize: 12,
              }}
              required
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
              Opportunity Type
            </label>
            <select
              name="opportunity_type"
              value={formData.opportunity_type}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 4,
                background: '#111827',
                border: '1px solid #1E2D45',
                color: '#F1F5F9',
                fontSize: 12,
              }}
            >
              <option>RFP</option>
              <option>RFI</option>
              <option>RFQ</option>
              <option>IFB</option>
              <option>IDIQ</option>
              <option>BPA</option>
              <option>Other</option>
            </select>
          </div>
        </div>

        {/* PSC (Product/Service Code) — drives the value estimate */}
        <div style={{ position: 'relative' }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
            PSC / Product-Service Code <span style={{ fontWeight: 400, color: '#6B7280' }}>(sets the value estimate)</span>
          </label>
          {formData.psc_code ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#0A0F1C', border: '1px solid #1E2D45', borderRadius: 4, padding: '6px 10px' }}>
              <span style={{ fontSize: 12, color: '#22D3EE' }}>{formData.psc_code}</span>
              <button type="button" onClick={() => { setFormData(p => ({ ...p, psc_code: '' })); setGovEstimate(null); }}
                style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
            </div>
          ) : (
            <>
              <input type="text" value={pscQuery} onChange={(e) => setPscQuery(e.target.value)} placeholder="Search PSC by code or title…"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 4, background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', fontSize: 12 }} />
              {pscResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, background: '#0A0F1C', border: '1px solid #1E2D45', borderRadius: 4, marginTop: 2, maxHeight: 180, overflowY: 'auto' }}>
                  {pscResults.map((r) => (
                    <div key={r.code} onClick={() => { setFormData(p => ({ ...p, psc_code: r.code })); setPscQuery(''); setPscResults([]); }}
                      style={{ padding: '5px 10px', fontSize: 12, color: '#F1F5F9', cursor: 'pointer', borderBottom: '1px solid #111827' }}>
                      <span style={{ color: '#22D3EE' }}>{r.code}</span> {r.title}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Value */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
            Estimated Value (USD)
          </label>
          <input
            type="number"
            name="opportunity_value_usd"
            value={formData.opportunity_value_usd}
            onChange={handleChange}
            placeholder="Leave blank to auto-estimate from PSC + office"
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 4,
              background: '#111827',
              border: '1px solid #1E2D45',
              color: '#F1F5F9',
              fontSize: 12,
            }}
          />
          {govEstimate && govEstimate.value ? (
            <div onClick={() => setFormData(p => ({ ...p, opportunity_value_usd: govEstimate.value }))}
              title={govEstimate.basis === 'org' ? 'Typical award for this PSC at this office' : 'DoD-wide typical award for this PSC'}
              style={{ marginTop: 4, fontSize: 10, color: '#22D3EE', cursor: 'pointer' }}>
              💡 Suggested {fmtUsd(govEstimate.value)}{govEstimate.basis === 'dod' ? ' (DoD-wide)' : ''} — click to use, or leave blank to apply on save
            </div>
          ) : (formData.psc_code && govEstimate && !govEstimate.value ? (
            <div style={{ marginTop: 4, fontSize: 10, color: '#6B7280' }}>No spend history for this PSC — will save blank</div>
          ) : null)}
        </div>

        {/* Description */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Detailed description of the opportunity..."
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 4,
              background: '#111827',
              border: '1px solid #1E2D45',
              color: '#F1F5F9',
              fontSize: 12,
              fontFamily: 'inherit',
              minHeight: '100px',
              resize: 'vertical',
            }}
          />
        </div>

        {/* NAICS Codes */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
            NAICS Codes
          </label>
          {formData.naics_codes.map((naics, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                value={naics}
                onChange={(e) => handleNaicsChange(idx, e.target.value)}
                placeholder="e.g., 541512"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 4,
                  background: '#111827',
                  border: '1px solid #1E2D45',
                  color: '#F1F5F9',
                  fontSize: 12,
                }}
              />
              {formData.naics_codes.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeNaicsField(idx)}
                  style={{
                    padding: '8px 12px',
                    background: '#EF4444',
                    border: 'none',
                    borderRadius: 4,
                    color: '#FFF',
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addNaicsField}
            style={{
              padding: '6px 12px',
              background: 'transparent',
              border: '1px dashed #22D3EE',
              borderRadius: 4,
              color: '#22D3EE',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            + Add NAICS Code
          </button>
        </div>

        {/* Verticals */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 8 }}>
            Matched Operational Domains
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
            {verticals.map(v => (
              <label key={v.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: selectedVerticals.includes(v.id) ? '#22D3EE22' : 'transparent',
                border: `1px solid ${selectedVerticals.includes(v.id) ? '#22D3EE' : '#1E2D45'}`,
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 11,
              }}>
                <input
                  type="checkbox"
                  checked={selectedVerticals.includes(v.id)}
                  onChange={() => toggleVertical(v.id)}
                  style={{ cursor: 'pointer' }}
                />
                {v.name}
              </label>
            ))}
          </div>
        </div>

        {/* Collaboration Partners */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 8 }}>
            Collaboration Partners (optional)
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {collaboratorOptions.map(partner => (
              <label key={partner} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: selectedCollaborators.includes(partner) ? '#22D3EE22' : 'transparent',
                border: `1px solid ${selectedCollaborators.includes(partner) ? '#22D3EE' : '#1E2D45'}`,
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 11,
              }}>
                <input
                  type="checkbox"
                  checked={selectedCollaborators.includes(partner)}
                  onChange={() => {
                    setSelectedCollaborators(prev =>
                      prev.includes(partner)
                        ? prev.filter(p => p !== partner)
                        : [...prev, partner]
                    );
                  }}
                  style={{ cursor: 'pointer' }}
                />
                {collaboratorLabels[partner] || partner}
              </label>
            ))}
          </div>
        </div>

        {/* Add Follow-On Opportunity */}
        {!createdOpportunityId && (
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              padding: '8px 12px',
              background: addFollowOn ? '#22D3EE22' : 'transparent',
              border: `1px solid ${addFollowOn ? '#22D3EE' : '#1E2D45'}`,
              borderRadius: 4,
            }}>
              <input
                type="checkbox"
                checked={addFollowOn}
                onChange={() => setAddFollowOn(!addFollowOn)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8' }}>
                Also add a follow-on opportunity
              </span>
            </label>
          </div>
        )}

        {/* Source URL */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
            Source URL
          </label>
          <input
            type="url"
            name="source_url"
            value={formData.source_url}
            onChange={handleChange}
            placeholder="https://..."
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 4,
              background: '#111827',
              border: '1px solid #1E2D45',
              color: '#F1F5F9',
              fontSize: 12,
            }}
          />
        </div>

        {/* Messages */}
        {success && (
          <div style={{
            padding: '12px',
            background: '#22D3EE22',
            border: '1px solid #22D3EE',
            borderRadius: 4,
            color: '#22D3EE',
            fontSize: 12,
          }}>
            {success}
          </div>
        )}

        {error && (
          <div style={{
            padding: '12px',
            background: '#EF444422',
            border: '1px solid #EF4444',
            borderRadius: 4,
            color: '#EF4444',
            fontSize: 12,
          }}>
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '12px',
            background: '#22D3EE',
            border: 'none',
            borderRadius: 4,
            color: '#0A0F1C',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            fontSize: 12,
            marginTop: 'auto',
          }}
        >
          {loading ? 'Creating...' : '✅ Create Opportunity'}
        </button>
      </form>
      )}

      {/* Follow-On Opportunity Form */}
      {createdOpportunityId && addFollowOn && (
      <form onSubmit={handleFollowOnSubmit} style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ padding: '12px', background: '#22D3EE22', border: '1px solid #22D3EE', borderRadius: 4, color: '#22D3EE', fontSize: 11 }}>
          ✅ Parent opportunity created successfully!
        </div>

        {/* Follow-On Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
              Follow-On Title *
            </label>
            <input
              type="text"
              value={followOnForm.title}
              onChange={(e) => setFollowOnForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Follow-on opportunity title"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 4,
                background: '#111827',
                border: '1px solid #1E2D45',
                color: '#F1F5F9',
                fontSize: 12,
                fontFamily: 'inherit',
              }}
              required
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
              Agency *
            </label>
            <input
              type="text"
              value={followOnForm.agency}
              onChange={(e) => setFollowOnForm(prev => ({ ...prev, agency: e.target.value }))}
              placeholder="e.g., Department of War"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 4,
                background: '#111827',
                border: '1px solid #1E2D45',
                color: '#F1F5F9',
                fontSize: 12,
                fontFamily: 'inherit',
              }}
              required
            />
          </div>
        </div>

        {/* Follow-On Dates and Value */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
              Response Deadline *
            </label>
            <input
              type="date"
              value={followOnForm.response_deadline}
              onChange={(e) => setFollowOnForm(prev => ({ ...prev, response_deadline: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 4,
                background: '#111827',
                border: '1px solid #1E2D45',
                color: '#F1F5F9',
                fontSize: 12,
              }}
              required
            />
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 4 }}>
              Estimated Value (USD)
            </label>
            <input
              type="number"
              value={followOnForm.opportunity_value_usd}
              onChange={(e) => setFollowOnForm(prev => ({ ...prev, opportunity_value_usd: parseFloat(e.target.value) || 0 }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 4,
                background: '#111827',
                border: '1px solid #1E2D45',
                color: '#F1F5F9',
                fontSize: 12,
              }}
            />
          </div>
        </div>

        {/* Follow-On Collaboration Partners */}
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94A3B8', marginBottom: 8 }}>
            Collaboration Partners (optional)
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {collaboratorOptions.map(partner => (
              <label key={partner} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: selectedCollaborators.includes(partner) ? '#22D3EE22' : 'transparent',
                border: `1px solid ${selectedCollaborators.includes(partner) ? '#22D3EE' : '#1E2D45'}`,
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 11,
              }}>
                <input
                  type="checkbox"
                  checked={selectedCollaborators.includes(partner)}
                  onChange={() => {
                    setSelectedCollaborators(prev =>
                      prev.includes(partner)
                        ? prev.filter(p => p !== partner)
                        : [...prev, partner]
                    );
                  }}
                  style={{ cursor: 'pointer' }}
                />
                {collaboratorLabels[partner] || partner}
              </label>
            ))}
          </div>
        </div>

        {/* Messages */}
        {success && (
          <div style={{
            padding: '12px',
            background: '#22D3EE22',
            border: '1px solid #22D3EE',
            borderRadius: 4,
            color: '#22D3EE',
            fontSize: 12,
          }}>
            {success}
          </div>
        )}

        {error && (
          <div style={{
            padding: '12px',
            background: '#EF444422',
            border: '1px solid #EF4444',
            borderRadius: 4,
            color: '#EF4444',
            fontSize: 12,
          }}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 'auto' }}>
          <button
            type="button"
            onClick={() => {
              setCreatedOpportunityId(null);
              setAddFollowOn(false);
              setFollowOnForm({
                title: '',
                agency: '',
                response_deadline: '',
                opportunity_value_usd: '',
              });
              setSelectedCollaborators([]);
            }}
            style={{
              flex: 1,
              padding: '12px',
              background: 'transparent',
              border: '1px solid #1E2D45',
              borderRadius: 4,
              color: '#94A3B8',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            ← Start Over
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px',
              background: '#22D3EE',
              border: 'none',
              borderRadius: 4,
              color: '#0A0F1C',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              fontSize: 12,
            }}
          >
            {loading ? 'Creating...' : '✅ Create Follow-On'}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}
